import apiClient, { handleApiError } from '../../utils/apiClient';
import type {
  MonthlyShiftSlotData,
  DailyShiftSlotGrouped,
  TimeSlot,
  ShiftSlotStatus,
  UrgentTarget,
  IncentiveSettingInput,
  SlotUrgentSettingInput,
} from '../../domain/dailyShiftSlot';

/**
 * APIレスポンス型定義
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * API月次シフト枠レスポンス型（camelCase）
 */
interface MonthlyShiftSlotApiResponse {
  year: number;
  month: number;
  status: ShiftSlotStatus;
  recruitmentStartAt: string | null;
  deadlineAt: string | null;
  days: Array<{
    date: string;
    isHoliday: boolean;
    holidayName?: string;
    dayOfWeek: number;
    status: ShiftSlotStatus;
    slots: Array<{
      id: string;
      startTime: string;
      endTime: string;
      recruitmentSlots: number;
      recruitedSlots?: number;
      totalReservationSlots: number;
      urgentSlots: number;
      confirmedSlots: number;
      shortageSlots: number;
    }>;
  }>;
}

/**
 * シフト枠更新リクエスト型（snake_case）
 */
interface UpdateSlotsApiRequest {
  slots: Array<{
    id: string;
    recruitment_slots?: number;
    urgent_slots?: number;
  }>;
}

/**
 * ステータス更新リクエスト型（snake_case）
 */
interface UpdateStatusApiRequest {
  year: number;
  month: number;
  status: ShiftSlotStatus;
  recruitment_start_at?: string | null;
  deadline_at?: string | null;
}

/**
 * 通知送信リクエスト型（snake_case）
 */
interface SendNotificationApiRequest {
  year: number;
  month: number;
  doctor_ids: string[];
}

/**
 * 急募設定保存リクエスト型（snake_case）
 *
 * インセンティブ・対象枠・LINE通知設定をまとめて保存する
 */
interface SaveUrgentSettingsApiRequest {
  year: number;
  month: number;
  incentive_amount: number;
  minimum_working_hours: number;
  start_date: string;
  end_date: string;
  slot_ids: string[];
  send_line_notification: boolean;
  notification_message: string;
}

/**
 * 単一枠 急募設定保存リクエスト型（snake_case）
 *
 * 対象枠はパスパラメータで決まるため year / month / 期間 / slot_ids を持たない
 */
interface SaveSlotUrgentSettingsApiRequest {
  incentive_amount: number;
  minimum_working_hours: number;
  send_line_notification: boolean;
  notification_message: string;
}

/**
 * 急募対象枠APIレスポンス型（camelCase・15項目）
 *
 * recruitedSlots（募集済み枠数＝応募実績）と recruitmentSlots（募集枠数＝定員）は別項目
 */
interface IncentiveUrgentTargetApiItem {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  isHoliday: boolean;
  holidayName: string | null;
  urgentSlots: number;
  recruitedSlots: number;
  recruitmentSlots: number;
  incentive: number | null;
  incentiveConditions: string | null;
  confirmedSlots: number;
  urgentNotifiedAt: string | null;
  confirmedDoctors: Array<{ id: number; name: string }>;
  applications: Array<{
    rank: number;
    doctorId: number;
    doctorName: string;
    appliedAt: string;
    status: '確定' | '補欠';
  }>;
}

/**
 * CSVインポートレスポンス型
 */
interface ImportCsvApiResponse {
  success: boolean;
  imported: number;
  errors: string[];
}

/**
 * CSVエクスポートレスポンス型
 */
interface ExportCsvApiResponse {
  success: boolean;
  csv_data: string;
  filename: string;
}

/**
 * APIレスポンス（camelCase）をフロントエンド形式に変換
 */
function toMonthlyShiftSlotData(response: MonthlyShiftSlotApiResponse): MonthlyShiftSlotData {
  return {
    year: response.year,
    month: response.month,
    status: response.status,
    recruitmentStartAt: response.recruitmentStartAt,
    deadlineAt: response.deadlineAt,
    days: response.days.map((day): DailyShiftSlotGrouped => ({
      date: day.date,
      isHoliday: day.isHoliday,
      holidayName: day.holidayName,
      dayOfWeek: day.dayOfWeek,
      status: day.status,
      slots: day.slots.map((slot): TimeSlot => ({
        id: slot.id,
        startTime: slot.startTime,
        endTime: slot.endTime,
        recruitmentSlots: slot.recruitmentSlots,
        recruitedSlots: slot.recruitedSlots ?? 0,
        totalReservationSlots: slot.totalReservationSlots,
        urgentSlots: slot.urgentSlots,
        confirmedSlots: slot.confirmedSlots,
        shortageSlots: slot.shortageSlots,
      })),
    })),
  };
}

/**
 * 日次シフト枠API（バックエンドAPIとの通信を担当）
 */
export const dailyShiftSlotApi = {
  /**
   * 月次シフト枠取得
   * @param year number 年
   * @param month number 月
   * @returns Promise<MonthlyShiftSlotData> 月次シフト枠データ
   */
  getByMonth: async (year: number, month: number): Promise<MonthlyShiftSlotData> => {
    try {
      const response = await apiClient.get<ApiResponse<MonthlyShiftSlotApiResponse>>(
        '/api/daily-shift-slots',
        { params: { year, month } }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '月次シフト枠の取得に失敗しました');
      }

      return toMonthlyShiftSlotData(response.data.data);
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * シフト枠一括更新
   * @param slots Array 更新データ配列
   * @returns Promise<void>
   */
  updateSlots: async (slots: Array<{
    id: string;
    recruitmentSlots?: number;
    urgentSlots?: number;
  }>): Promise<void> => {
    try {
      const request: UpdateSlotsApiRequest = {
        slots: slots.map(slot => ({
          id: slot.id,
          recruitment_slots: slot.recruitmentSlots,
          urgent_slots: slot.urgentSlots,
        })),
      };

      const response = await apiClient.put<ApiResponse<void>>(
        '/api/daily-shift-slots/batch',
        request
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'シフト枠の更新に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * ステータス更新
   * @param data UpdateStatusApiRequest ステータス更新データ
   * @returns Promise<void>
   */
  updateStatus: async (data: {
    year: number;
    month: number;
    status: ShiftSlotStatus;
    recruitmentStartAt?: string | null;
    deadlineAt?: string | null;
  }): Promise<void> => {
    try {
      const request: UpdateStatusApiRequest = {
        year: data.year,
        month: data.month,
        status: data.status,
        recruitment_start_at: data.recruitmentStartAt,
        deadline_at: data.deadlineAt,
      };

      const response = await apiClient.put<ApiResponse<void>>(
        '/api/daily-shift-slots/status',
        request
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || 'ステータスの更新に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 空き枠紹介通知送信
   * @param data object 通知データ
   * @returns Promise<void>
   */
  sendVacancyNotification: async (data: {
    year: number;
    month: number;
    doctorIds: string[];
  }): Promise<void> => {
    try {
      const request: SendNotificationApiRequest = {
        year: data.year,
        month: data.month,
        doctor_ids: data.doctorIds,
      };

      const response = await apiClient.post<ApiResponse<void>>(
        '/api/daily-shift-slots/notify/vacancy',
        request
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '空き枠紹介通知の送信に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 急募枠通知送信
   * @param data object 通知データ
   * @returns Promise<void>
   */
  sendUrgentNotification: async (data: {
    year: number;
    month: number;
    doctorIds: string[];
  }): Promise<void> => {
    try {
      const request: SendNotificationApiRequest = {
        year: data.year,
        month: data.month,
        doctor_ids: data.doctorIds,
      };

      const response = await apiClient.post<ApiResponse<void>>(
        '/api/daily-shift-slots/notify/urgent',
        request
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '急募枠通知の送信に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 急募対象枠一覧取得
   *
   * year / month はバックエンドで必須。未指定の場合は HTTP 400 となる
   * @param year number 年
   * @param month number 月
   * @returns Promise<UrgentTarget[]> 急募対象枠一覧
   */
  getIncentiveUrgentTargets: async (year: number, month: number): Promise<UrgentTarget[]> => {
    try {
      const response = await apiClient.get<ApiResponse<IncentiveUrgentTargetApiItem[]>>(
        '/api/daily-shift-slots/incentive-urgent-targets',
        { params: { year, month } }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || '急募対象枠の取得に失敗しました');
      }

      return response.data.data.map((item): UrgentTarget => ({
        id: item.id,
        date: item.date,
        startTime: item.startTime,
        endTime: item.endTime,
        isHoliday: item.isHoliday,
        holidayName: item.holidayName,
        urgentSlots: item.urgentSlots,
        recruitedSlots: item.recruitedSlots,
        recruitmentSlots: item.recruitmentSlots,
        incentive: item.incentive,
        incentiveConditions: item.incentiveConditions,
        confirmedSlots: item.confirmedSlots,
        urgentNotifiedAt: item.urgentNotifiedAt,
        confirmedDoctors: item.confirmedDoctors,
        applications: item.applications,
      }));
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 医師の直接確定（応募中の医師より優先して確定）
   *
   * ※ バックエンド側のエンドポイントは新設予定のため、実装されるまではエラーになる
   * @param slotId string 日次シフト枠ID
   * @param doctorId number 医師ID
   * @returns Promise<void>
   */
  confirmDoctorDirect: async (slotId: string, doctorId: number): Promise<void> => {
    try {
      const response = await apiClient.post<ApiResponse<void>>(
        `/api/daily-shift-slots/${slotId}/confirm-doctor`,
        { doctor_id: doctorId }
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '医師の直接確定に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 急募枠への応募をキャンセルする（対象医師の医師シフトを削除）
   *
   * バックエンド側で対象枠の (日付・開始・終了) に完全一致する医師シフトを1件だけ
   * 論理削除し、募集済み枠数を再集計する。
   * @param slotId string 日次シフト枠ID
   * @param doctorId number 医師ID（応募一覧の doctorId）
   * @returns Promise<void>
   */
  cancelApplication: async (slotId: string, doctorId: number): Promise<void> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(
        `/api/daily-shift-slots/${slotId}/applications/${doctorId}`
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '応募のキャンセルに失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 単一枠の急募設定を保存する（急募詳細ダイアログ用）
   *
   * 一括版 saveUrgentSettings と違い、急募枠数（urgentSlots）は変更されない。
   * notification_message は加工されずそのまま LINE へ送信されるため、
   * 変数の置換は呼び出し側で済ませてから渡すこと。
   * @param slotId string 日次シフト枠ID
   * @param input SlotUrgentSettingInput 急募設定入力
   * @returns Promise<void>
   */
  saveSlotUrgentSettings: async (slotId: string, input: SlotUrgentSettingInput): Promise<void> => {
    try {
      const request: SaveSlotUrgentSettingsApiRequest = {
        incentive_amount: input.incentiveAmount,
        minimum_working_hours: input.minimumWorkingHours,
        send_line_notification: input.sendLineNotification,
        notification_message: input.notificationMessage,
      };

      const response = await apiClient.post<ApiResponse<void>>(
        `/api/daily-shift-slots/${slotId}/urgent-settings`,
        request
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '急募設定の保存に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * 急募設定を保存する（インセンティブ・対象枠・LINE通知設定をまとめて保存）
   *
   * ※ バックエンド側のエンドポイントは新設予定のため、実装されるまではエラーになる
   * @param input IncentiveSettingInput 急募設定入力
   * @returns Promise<void>
   */
  saveUrgentSettings: async (input: IncentiveSettingInput): Promise<void> => {
    try {
      const request: SaveUrgentSettingsApiRequest = {
        year: input.year,
        month: input.month,
        incentive_amount: input.incentiveAmount,
        minimum_working_hours: input.minimumWorkingHours,
        start_date: input.startDate,
        end_date: input.endDate,
        slot_ids: input.slotIds,
        send_line_notification: input.sendLineNotification ?? false,
        notification_message: input.notificationMessage ?? '',
      };

      const response = await apiClient.post<ApiResponse<void>>(
        '/api/daily-shift-slots/urgent-settings',
        request
      );

      if (!response.data.success) {
        throw new Error(response.data.error?.message || '急募設定の保存に失敗しました');
      }
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * CSVインポート
   * @param year number 年
   * @param month number 月
   * @param csvData string CSVデータ
   * @returns Promise<{ success: boolean; importedCount: number; errors: string[] }>
   */
  importCsv: async (year: number, month: number, csvData: string): Promise<{
    success: boolean;
    importedCount: number;
    errors: string[];
  }> => {
    try {
      const response = await apiClient.post<ImportCsvApiResponse>(
        '/api/daily-shift-slots/import',
        { year, month, csvData }
      );

      if (!response.data.success) {
        throw new Error('CSVインポートに失敗しました');
      }

      return {
        success: response.data.success,
        importedCount: response.data.imported,
        errors: response.data.errors,
      };
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },

  /**
   * CSVエクスポート
   * @param year number 年
   * @param month number 月
   * @returns Promise<{ success: boolean; csvData: string; filename: string }>
   */
  exportCsv: async (year: number, month: number): Promise<{
    success: boolean;
    csvData: string;
    filename: string;
  }> => {
    try {
      const response = await apiClient.get<ApiResponse<ExportCsvApiResponse>>(
        '/api/daily-shift-slots/export',
        { params: { year, month } }
      );

      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error?.message || 'CSVエクスポートに失敗しました');
      }

      return {
        success: response.data.data.success,
        csvData: response.data.data.csv_data,
        filename: response.data.data.filename,
      };
    } catch (error) {
      const errorMessage = handleApiError(error);
      throw new Error(errorMessage);
    }
  },
};

export default dailyShiftSlotApi;
